import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase, type Almacen } from "./supabase";

type Ctx = {
  almacenes: Almacen[];
  almacen: Almacen | null;
  setAlmacenId: (id: string) => void;
  cargando: boolean;
  /** true cuando hay más de un almacén (la UI muestra el selector). */
  multi: boolean;
};

const AlmacenCtx = createContext<Ctx | null>(null);
const CLAVE = "eyeia.almacen";

export function AlmacenProvider({ children }: { children: ReactNode }) {
  const { data, isLoading } = useQuery({
    queryKey: ["almacenes"],
    queryFn: async () => {
      const { data, error } = await supabase.from("almacenes").select("*").eq("activo", true).order("codigo");
      if (error) throw error;
      return data;
    },
  });
  const almacenes = data ?? [];
  const [id, setId] = useState<string | null>(() => {
    try { return localStorage.getItem(CLAVE); } catch { return null; }
  });

  // Si el guardado ya no existe, cae al primero.
  useEffect(() => {
    if (almacenes.length === 0) return;
    if (!id || !almacenes.some((a) => a.id === id)) {
      setId(almacenes[0].id);
    }
  }, [almacenes, id]);

  const valor = useMemo<Ctx>(() => ({
    almacenes,
    almacen: almacenes.find((a) => a.id === id) ?? almacenes[0] ?? null,
    setAlmacenId: (nuevo) => {
      setId(nuevo);
      try { localStorage.setItem(CLAVE, nuevo); } catch { /* sin storage: no pasa nada */ }
    },
    cargando: isLoading,
    multi: almacenes.length > 1,
  }), [almacenes, id, isLoading]);

  return <AlmacenCtx.Provider value={valor}>{children}</AlmacenCtx.Provider>;
}

export function useAlmacen(): Ctx {
  const ctx = useContext(AlmacenCtx);
  if (!ctx) throw new Error("useAlmacen fuera de AlmacenProvider");
  return ctx;
}
